import React, { useEffect, useRef, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Checkbox, CircularProgress, Divider,
  FormControlLabel, MenuItem, Select, Tab, Tabs, TextField, Typography,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'
import { useViewerStudent } from '../../auth/ViewAsContext'
import { readFunctionError } from '../../lib/functionError'
import Markdown from '../../components/Markdown'
import ImportantDatesCard from '../../components/fair/ImportantDatesCard'
import JournalTab from './JournalTab'
import JudgeReadinessPanel, { JudgeReadinessResult } from '../../components/student/JudgeReadinessPanel'
import { Project } from '../../types'

// Step 5 — Design Experiments (Scientific track) / Develop Products (Engineering
// track). Sourced from ardent-api Lesson 6 "Design Experiments and Develop
// Products" (see docs/step5-source-content.md). v1: Learn + Design (student
// work surface + mentor approve/send-back) — a genuinely usable loop. Per-field
// AI feedback ("Check my …") and a Journal tab are deferred fast-follows, same
// as how Steps 1-3 got AI review layered on after their work surface shipped —
// see memory step5-src-direction. Mirrors the Step 1–4 page shell.

type Track  = 'scientific_method' | 'engineering_design'
type Status = 'draft' | 'submitted' | 'approved_draft' | 'completed'

interface Dim   { state: 'ok' | 'caution' | 'thinking'; note: string }
interface AIEval { shape: Dim; anchor: Dim; ai_disclaimer?: string }

interface Payload {
  // Scientific
  design_type?:           string
  materials?:              string   // shared key: "Materials" (sci) / "Materials & Tools" (eng)
  apparatus?:              string
  procedure?:              string
  data_collection_method?: string
  data_sheet_url?:         string
  analysis_plan?:          string
  error_analysis?:         string
  // Engineering — anchored on docs "6.2.2 Engineering Design Document" guide
  design_description?: string   // how the design/product works — form, components, mechanism
  build_procedure?:    string   // "Development & Testing Plan" for eng; "Build & iteration" was folded in
  test_plan?:          string   // "Performance Criteria & Iteration Ideas" for eng
  // Write-Up (both tracks) — framing content that feeds the eventual Paper.
  // Not part of the Design submit gate; seeded editable from Step 3/4 on
  // first entry, per the seed-don't-just-reference principle.
  purpose_rq?:           string
  target_user?:           string   // engineering only — "Target User Description"
  variables_hypothesis?: string   // labeled "User Needs & MVP" for engineering
  introduction?:          string
  acknowledgement?:       string
  references?:            string
  paper_draft?:           string   // AI-assembled draft of the full document, from Paper tab
  ai_feedback?: Record<string, AIEval>
  judge_readiness?: JudgeReadinessResult   // last judge-readiness (winning-bar) result, Design tab
}

// Seeded starting content for the Write-Up tab, computed once from Step 3/4.
interface WriteUpSeed {
  purposeRq: string; targetUser: string; variablesHypothesis: string; introduction: string; references: string
}

export default function Step5Page() {
  const { session } = useAuth()
  const { studentId: viewerStudentId, viewerIsStudent, loading: vasLoading } = useViewerStudent()
  const navigate = useNavigate()

  const [project, setProject] = useState<Project | null>(null)
  const [track,   setTrack]   = useState<Track>('scientific_method')
  const [seed,    setSeed]    = useState<Payload>({})
  const [status,  setStatus]  = useState<Status>('draft')
  const [mentorNote, setMentorNote] = useState<string | null>(null)
  const [sectionComments, setSectionComments] = useState<Record<string, string>>({})
  const [carryApparatus, setCarryApparatus] = useState<string>('')   // Step 4 tool/tools text, seeded once
  const [carryDesignDescription, setCarryDesignDescription] = useState<string>('')   // eng only — Step 4 solution text
  const [writeUpSeed, setWriteUpSeed] = useState<WriteUpSeed>({ purposeRq: '', targetUser: '', variablesHypothesis: '', introduction: '', references: '' })
  const [submissionNote, setSubmissionNote] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [tab,     setTab]     = useState<'learn' | 'design' | 'writeup' | 'src' | 'paper' | 'journal' | 'review'>('learn')
  // Spinner (which unmounts the form) only on first load / actual student
  // switch — never on a same-student refetch, which would discard unsaved input.
  const loadedStudentRef = useRef<string | null>(null)

  useEffect(() => {
    async function load() {
      if (vasLoading) return
      if (!viewerStudentId) { navigate('/'); return }
      if (loadedStudentRef.current !== viewerStudentId) setLoading(true)
      const { data: projects } = await supabase
        .from('project').select('*').eq('student_id', viewerStudentId).eq('status', 'active')
        .order('created_at', { ascending: false }).limit(1)
      const proj = projects?.[0] ?? null
      if (!proj) { setLoading(false); return }
      setProject(proj as Project)

      const [{ data: rq }, { data: hd }, { data: ed }, { data: bg }] = await Promise.all([
        supabase.from('research_question').select('track_committed').eq('project_id', proj.project_id).maybeSingle(),
        supabase.from('hypothesis_design').select('track, payload').eq('project_id', proj.project_id).maybeSingle(),
        supabase.from('experiment_design').select('status, payload, mentor_note, mentor_section_comments, submission_note').eq('project_id', proj.project_id).maybeSingle(),
        supabase.from('background_research').select('payload').eq('project_id', proj.project_id).maybeSingle(),
      ])
      const trackVal = ((rq as any)?.track_committed as Track) ?? 'scientific_method'
      setTrack(trackVal)

      // Carry forward Step 4 content as starting points — editable, not a
      // locked reference (see memory feedback-seed-editable-not-readonly-
      // reference). Engineering mapping anchored on docs "6.2.2 Engineering
      // Design Document" guide against Step 4's actual eng payload shape.
      const hdPayload = ((hd as any)?.payload ?? {}) as Record<string, any>
      if (trackVal === 'engineering_design') {
        setCarryApparatus((hdPayload.tools ?? '').trim())               // → Design tab "Materials & Tools"
        setCarryDesignDescription((hdPayload.solution ?? '').trim())    // → Design tab "Design Description"
      } else {
        const blocks = Array.isArray(hdPayload.blocks) ? hdPayload.blocks : []
        const toolBlock = blocks.find((b: any) => b?.needs_tool && (b.tool ?? '').trim())
        setCarryApparatus((toolBlock?.tool ?? '').trim())
      }

      // Write-Up seeds — same seed-don't-just-reference principle, pulling
      // from the MOST-refined prior version: Step 3's refined_question (not
      // Step 2's raw draft), and Step 4's committed hypothesis/requirements.
      const bgPayload = ((bg as any)?.payload ?? {}) as Record<string, any>
      const purposeRq = (bgPayload.refined_question ?? '').trim()
      const paperText = (v: any) => v && typeof v === 'object'
        ? [v.title, v.url, v.abstract].filter((x: any) => typeof x === 'string' && x.trim()).join(' — ') : ''
      const references = [paperText(bgPayload.review_paper), paperText(bgPayload.model_paper)].filter(Boolean).join('\n')
      let targetUser = ''
      let variablesHypothesis = ''
      let introduction = ''
      if (trackVal === 'engineering_design') {
        // "Target User Description" — Step 4's users_needs already bundles
        // who-they-are + why-they-care (or performance_goal if no human user);
        // both new/target_user and needs/variables_hypothesis seed from it —
        // the write-up is where the student separates and elaborates them.
        targetUser = (hdPayload.for_human_user === false ? hdPayload.performance_goal : hdPayload.users_needs ?? '').trim()
        variablesHypothesis = [
          hdPayload.users_needs && `User needs: ${hdPayload.users_needs}`,
          hdPayload.mvp && `MVP: ${hdPayload.mvp}`,
        ].filter(Boolean).join('\n')
        const introParts = [bgPayload.significance, bgPayload.key_concepts, bgPayload.principles, bgPayload.what_research_shows]
          .map((s: any) => (typeof s === 'string' ? s.trim() : '')).filter(Boolean)
        if (hdPayload.prior_art) introParts.push(`Existing solutions and their limitations: ${hdPayload.prior_art}`)
        introduction = introParts.join('\n\n')
      } else {
        const blocks = Array.isArray(hdPayload.blocks) ? hdPayload.blocks : []
        variablesHypothesis = [
          hdPayload.iv && `Independent variable: ${hdPayload.iv}`,
          hdPayload.cv && `Controlled variables: ${hdPayload.cv}`,
          ...blocks.map((b: any, i: number) => `Hypothesis ${i + 1}: ${b.hypothesis ?? ''} — because: ${b.rationale ?? ''}`),
        ].filter(Boolean).join('\n')
        const introParts = [bgPayload.significance, bgPayload.key_concepts, bgPayload.principles, bgPayload.what_research_shows]
          .map((s: any) => (typeof s === 'string' ? s.trim() : '')).filter(Boolean)
        introduction = introParts.join('\n\n')
      }
      setWriteUpSeed({ purposeRq, targetUser, variablesHypothesis, introduction, references })

      setStatus(((ed as any)?.status as Status) ?? 'draft')
      setMentorNote((ed as any)?.mentor_note ?? null)
      setSectionComments(((ed as any)?.mentor_section_comments ?? {}) as Record<string, string>)
      setSeed(((ed as any)?.payload ?? {}) as Payload)
      setSubmissionNote((ed as any)?.submission_note ?? '')

      loadedStudentRef.current = viewerStudentId
      setLoading(false)
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, viewerStudentId, vasLoading])

  if (loading) return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
  if (!project) return <Box sx={{ p: 3 }}><Alert severity="info">No active project found.</Alert></Box>

  const currentStep = project.current_step ?? 1
  if (currentStep < 5) {
    return (
      <Box sx={{ p: 3, maxWidth: 640 }}>
        <Alert severity="info" icon={<LockOutlinedIcon />}>
          <strong>Step 5 unlocks after your mentor approves Step 4.</strong> Finish your Hypothesis first.
        </Alert>
      </Box>
    )
  }

  const isEngineering = track === 'engineering_design'

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
        Step 5 — {isEngineering ? 'Develop Products' : 'Design Experiments'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isEngineering
          ? 'Turn your design requirements into a full development plan for your solution.'
          : 'Turn your hypothesis into a full experimental design — the research proposal you\'ll submit for approval.'}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }} variant="scrollable" scrollButtons="auto">
        <Tab label="Learn" value="learn" />
        <Tab label="Design" value="design" />
        <Tab label="Write-Up" value="writeup" />
        <Tab label="SRC Review" value="src" />
        <Tab label="Paper" value="paper" />
        <Tab label="Journal" value="journal" />
        <Tab label="Mentor Review" value="review" />
      </Tabs>

      {tab === 'learn' && (isEngineering ? <EngLearnTab /> : <LearnTab />)}
      {/* Kept mounted across all three sub-tabs (hidden when off design/writeup/
          paper) so in-progress edits on any are never unmounted and re-seeded
          from a stale payload on tab switch — same pattern as Step 4's
          hypothesis/tool. */}
      <Box sx={{ display: (tab === 'design' || tab === 'writeup' || tab === 'paper') ? 'block' : 'none' }}>
        <DesignWork
          projectId={project.project_id}
          studentId={viewerStudentId!}
          track={track}
          seed={seed}
          carryApparatus={carryApparatus}
          carryDesignDescription={carryDesignDescription}
          writeUpSeed={writeUpSeed}
          initialPaperDraft={seed.paper_draft ?? ''}
          initialSubmissionNote={submissionNote}
          status={status}
          mentorNote={mentorNote}
          editable={viewerIsStudent}
          view={tab === 'writeup' ? 'writeup' : tab === 'paper' ? 'paper' : 'design'}
          onStatus={(s, note) => { setStatus(s); if (note !== undefined) setMentorNote(note) }}
          onOpenPaper={() => setTab('paper')}
        />
      </Box>
      {tab === 'src' && (
        <SrcReviewTab projectId={project.project_id} studentId={viewerStudentId!} editable={viewerIsStudent} />
      )}
      {tab === 'journal' && (
        <JournalTab projectId={project.project_id} studentId={viewerStudentId!} />
      )}
      {tab === 'review' && (
        <DesignMentorReviewTab status={status} mentorNote={mentorNote} sectionComments={sectionComments} />
      )}
    </Box>
  )
}

// ── Section metadata ─────────────────────────────────────────────────────────
type TextFieldKey = Exclude<keyof Payload, 'ai_feedback' | 'judge_readiness'>
const SCI_SECTIONS: { key: TextFieldKey; label: string; helper: string; rows?: number }[] = [
  { key: 'design_type', label: 'Experimental design type',
    helper: 'What type of experimental design fits your hypothesis (e.g. controlled comparison, before/after, correlational study) — and why.', rows: 3 },
  { key: 'materials', label: 'Materials',
    helper: 'List everything you\'ll use — the specific type, amount, and where you\'ll get it. Be as specific as possible.', rows: 4 },
  { key: 'apparatus', label: 'Apparatus & software',
    helper: 'How your materials become the apparatus that runs your experiment — or the dataset/software/code you\'re using.', rows: 4 },
  { key: 'procedure', label: 'Procedure',
    helper: 'Step-by-step, detailed enough that another student could replicate your experiment exactly.', rows: 6 },
  { key: 'data_collection_method', label: 'Data collection method',
    helper: 'What you\'ll record, how often, and how.', rows: 3 },
  { key: 'analysis_plan', label: 'Data analysis plan',
    helper: 'How you\'ll analyze the data once collected (e.g. averages, graphs, statistical tests).', rows: 3 },
  { key: 'error_analysis', label: 'Error analysis & improvements',
    helper: 'Identify 2-3 realistic sources of error in your design, and how you\'d reduce them.', rows: 4 },
]
// Anchored on docs "6.2.2 Engineering Design Document" guide's 6 core
// sections, mapped onto Step 4's actual eng payload (problem/users_needs/
// performance_goal/prior_art/requirements/mvp/tools/solution). "Design
// Description and Materials" splits into 2 fields here (same granularity as
// the Scientific track's separate Materials/Apparatus); "Development and
// Testing Plan" merges what used to be separate build+test fields.
const ENG_SECTIONS: { key: TextFieldKey; label: string; helper: string; rows?: number }[] = [
  { key: 'design_description', label: 'Design description',
    helper: 'How your design/product works — its physical form, key components, and how they function together. Include labeled sketches or components if you have them.', rows: 5 },
  { key: 'materials', label: 'Materials & tools',
    helper: 'The specific parts, materials, and tools your design uses — real product names where possible.', rows: 4 },
  { key: 'build_procedure', label: 'Development & testing plan',
    helper: 'How you\'ll build your prototype, test it, and revise it — timeline, benchmarks, and testing methods.', rows: 6 },
  { key: 'test_plan', label: 'Performance criteria & iteration ideas',
    helper: 'Specific, measurable success criteria (2-3 metrics) validating your Step 4 requirements, and at least one idea for a future iteration.', rows: 4 },
]
// Write-Up sections — framing content that feeds the eventual Paper, not part
// of the Design submit gate. Engineering gets an extra "Target User" section
// (doc's 3-part structure: Problem & Purpose / Target User / Needs & MVP) —
// Scientific has no user-description analog, so it stays at 2 core fields.
function writeUpSections(isEng: boolean): { key: TextFieldKey; label: string; helper: string; rows?: number }[] {
  return [
    { key: 'purpose_rq', label: isEng ? 'Problem statement & purpose' : 'Purpose & research question',
      helper: 'A refined restatement of your Step 3 research — sharper and more specific than your Step 2 draft.', rows: 3 },
    ...(isEng ? [{ key: 'target_user' as TextFieldKey, label: 'Target user description',
      helper: 'Who is this for? Their situation, age/experience/environment, and why they\'d care about this product.', rows: 3 }] : []),
    { key: 'variables_hypothesis', label: isEng ? 'User needs & MVP' : 'Variables & hypothesis',
      helper: isEng
        ? 'The specific needs your users have, and how your MVP (minimal viable product) concept addresses them.'
        : 'A refined restatement of your Step 4 variables and hypothesis.', rows: 4 },
    { key: 'introduction', label: 'Introduction',
      helper: '0.5–1 page background summary — why this matters and what\'s already known.', rows: 6 },
    { key: 'acknowledgement', label: 'Acknowledgement',
      helper: 'Mentors, teachers, or others who supported your work.', rows: 2 },
    { key: 'references', label: 'References',
      helper: 'All sources you cite in the body of your writing.', rows: 4 },
  ]
}
// ── Feedback rendering (mirrors Step 3/4's FeedbackBlock) ───────────────────────
function FeedbackBlock({ feedback, stale }: { feedback: AIEval; stale: boolean }) {
  const color = (s: string) => s === 'ok' ? 'success.main' : s === 'caution' ? 'warning.main' : 'error.main'
  const bg    = (s: string) => s === 'ok' ? 'rgba(46,125,50,0.08)' : s === 'caution' ? 'rgba(237,108,2,0.08)' : 'rgba(198,40,40,0.08)'
  const glyph = (s: string) => s === 'ok' ? '✓' : s === 'caution' ? '⚠' : '?'
  const dims: Array<{ key: 'shape' | 'anchor'; label: string }> = [
    { key: 'shape', label: 'Shape' }, { key: 'anchor', label: 'Anchor' },
  ]
  return (
    <Box sx={{ mt: 1.5, opacity: stale ? 0.55 : 1 }}>
      {dims.map(d => {
        const dim = feedback[d.key]; if (!dim) return null
        return (
          <Box key={d.key} sx={{ display: 'flex', gap: 1, p: 1.25, mb: 0.75, borderRadius: 1, bgcolor: bg(dim.state), border: '1px solid', borderColor: `${color(dim.state)}40` }}>
            <Typography sx={{ color: color(dim.state), fontWeight: 700, minWidth: 18, textAlign: 'center' }}>{glyph(dim.state)}</Typography>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: color(dim.state), display: 'block' }}>{d.label}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{dim.note}</Typography>
            </Box>
          </Box>
        )
      })}
      {feedback.ai_disclaimer && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>ⓘ {feedback.ai_disclaimer}</Typography>
      )}
    </Box>
  )
}

function GroupFeedback({ name, canEvaluate, evaluating, feedback, stale, disabled, onEvaluate }: {
  name: string; canEvaluate: boolean; evaluating: boolean
  feedback?: AIEval; stale: boolean; disabled?: boolean; onEvaluate: () => void
}) {
  return (
    <Box sx={{ mt: 1 }}>
      {!disabled && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={onEvaluate} disabled={evaluating || !canEvaluate}>
            {evaluating ? <><CircularProgress size={14} sx={{ mr: 1 }} />Checking…</> : feedback ? `Re-check my ${name}` : `Check my ${name}`}
          </Button>
          {stale && feedback && <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Changed — re-check?</Typography>}
        </Box>
      )}
      {feedback && <FeedbackBlock feedback={feedback} stale={stale} />}
    </Box>
  )
}

function printPaper(body: string) {
  const w = window.open('', '_blank', 'width=820,height=1000')
  if (!w) return
  const safe = body.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string))
  w.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>Research Plan</title>` +
    `<style>body{font-family:Georgia,'Times New Roman',serif;line-height:1.6;max-width:720px;` +
    `margin:40px auto;padding:0 24px;color:#111}pre{white-space:pre-wrap;word-wrap:break-word;` +
    `font-family:inherit;font-size:14px}@media print{body{margin:0}}</style></head>` +
    `<body><pre>${safe}</pre></body></html>`,
  )
  w.document.close(); w.focus()
  setTimeout(() => w.print(), 250)
}

// ── Design tab — student work surface ────────────────────────────────────────
function DesignWork({
  projectId, studentId, track, seed, carryApparatus, carryDesignDescription, writeUpSeed, initialPaperDraft, initialSubmissionNote,
  status, mentorNote, editable, view, onStatus, onOpenPaper,
}: {
  projectId: string; studentId: string; track: Track; seed: Payload; carryApparatus: string
  carryDesignDescription: string
  writeUpSeed: WriteUpSeed
  initialPaperDraft: string; initialSubmissionNote: string
  status: Status; mentorNote: string | null; editable: boolean
  view: 'design' | 'writeup' | 'paper'
  onStatus: (s: Status, note?: string | null) => void
  onOpenPaper: () => void
}) {
  const isEng = track === 'engineering_design'
  const designSections = isEng ? ENG_SECTIONS : SCI_SECTIONS
  const sections = view === 'writeup' ? writeUpSections(isEng) : designSections

  const [fields, setFields] = useState<Payload>(() => {
    // Seed the apparatus/materials field from Step 4's tool text, and the
    // Write-Up fields from Step 3/4's most-refined content — each seeds
    // independently the first time IT is empty, so a student who saves Design
    // first and only opens Write-Up later still gets seeded (not gated on
    // whether the row exists at all). Editable from there, never re-clobbered.
    const base: Payload = { ...seed }
    if (!isEng && !(base.apparatus ?? '').trim()) base.apparatus = carryApparatus
    if (isEng && !(base.materials ?? '').trim()) base.materials = carryApparatus
    if (isEng && !(base.design_description ?? '').trim()) base.design_description = carryDesignDescription
    if (!(base.purpose_rq ?? '').trim()) base.purpose_rq = writeUpSeed.purposeRq
    if (isEng && !(base.target_user ?? '').trim()) base.target_user = writeUpSeed.targetUser
    if (!(base.variables_hypothesis ?? '').trim()) base.variables_hypothesis = writeUpSeed.variablesHypothesis
    if (!(base.introduction ?? '').trim()) base.introduction = writeUpSeed.introduction
    if (!(base.references ?? '').trim()) base.references = writeUpSeed.references
    return base
  })
  const [dataSheetUrl, setDataSheetUrl] = useState(seed.data_sheet_url ?? '')
  const [feedback,   setFeedback]   = useState<Record<string, AIEval>>(seed.ai_feedback ?? {})
  const [staleSet,   setStaleSet]   = useState<Set<string>>(new Set())
  const [evaluating, setEvaluating] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error,       setError]     = useState<string | null>(null)
  const [saveState,   setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [paperDraft,  setPaperDraft]  = useState(initialPaperDraft)
  const [generating,  setGenerating]  = useState(false)
  const [submissionNote, setSubmissionNote] = useState(initialSubmissionNote)
  const [judge,        setJudge]        = useState<JudgeReadinessResult | null>(seed.judge_readiness ?? null)
  const [judgeLoading, setJudgeLoading] = useState(false)

  const isReadOnly  = status !== 'draft' || !editable
  const isReturning = status === 'draft' && !!mentorNote

  function markStale(key: string) {
    setStaleSet(prev => { if (prev.has(key)) return prev; const n = new Set(prev); n.add(key); return n })
  }

  function updateField(key: TextFieldKey, value: string) {
    setFields(prev => ({ ...prev, [key]: value }))
    markStale(key)
  }

  function payload(): Payload {
    return { ...fields, data_sheet_url: dataSheetUrl, ai_feedback: feedback, paper_draft: paperDraft, judge_readiness: judge ?? undefined }
  }

  async function save(extra?: Partial<Payload>) {
    if (isReadOnly) return
    setSaveState('saving')
    const { error: upErr } = await supabase.from('experiment_design').upsert({
      project_id: projectId, student_id: studentId, track,
      payload: { ...payload(), ...extra }, updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id' })
    setSaveState(upErr ? 'idle' : 'saved')
  }

  const firstRun = useRef(true)
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return }
    if (isReadOnly) return
    const t = setTimeout(() => { save() }, 900)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(fields), dataSheetUrl, paperDraft])

  async function generatePaper() {
    if (generating) return
    setGenerating(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step5-generate-paper', {
        body: { project_id: projectId, student_id: studentId, payload: { ...payload(), track } },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      const text = (data.paper as string) ?? ''
      setPaperDraft(text)
      await save({ paper_draft: text })
    } catch (e: any) {
      setError(e.message ?? 'Could not generate a draft — please try again.')
    } finally {
      setGenerating(false)
    }
  }

  async function evaluate(key: string) {
    if (evaluating) return
    setEvaluating(key); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step5-evaluate-field', {
        body: { project_id: projectId, student_id: studentId, group: key, payload: payload() },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      const next = { ...feedback, [key]: data.evaluation as AIEval }
      setFeedback(next)
      setStaleSet(prev => { if (!prev.has(key)) return prev; const n = new Set(prev); n.delete(key); return n })
      await save({ ai_feedback: next })
    } catch (e: any) {
      setError(e.message ?? 'Could not get AI feedback — please try again.')
    } finally {
      setEvaluating(null)
    }
  }

  // "Judge-readiness" — holistic winning-bar coverage of the whole Design tab
  // against questions judges commonly ask (step5-judge-readiness).
  const judgeAnchor = ((isEng ? fields.design_description : fields.procedure) ?? '').trim()
  async function runJudgeReadiness() {
    if (judgeLoading) return
    setJudgeLoading(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step5-judge-readiness', {
        body: { project_id: projectId, student_id: studentId, payload: payload() },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      const result = data.readiness as JudgeReadinessResult
      setJudge(result)
      await save({ judge_readiness: result })
    } catch (e: any) {
      setError(e.message ?? 'Could not run the check — please try again.')
    } finally {
      setJudgeLoading(false)
    }
  }

  // Submission gate covers only the Design sections — Write-Up is framing
  // content for the eventual Paper, not gated the same way (mirrors the
  // Prompt Guide's own sequencing: Intro/Ack/References get added when
  // assembling the FULL document, after the core design sections are done).
  const readyToSubmit = designSections.every(s => (fields[s.key] ?? '').trim().length >= 10)

  async function submit() {
    if (submitting || !readyToSubmit) return
    setSubmitting(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step5-submit', {
        body: { project_id: projectId, payload: payload(), track, submission_note: submissionNote },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      onStatus('submitted', null)
    } catch (e: any) {
      setError(e.message ?? 'Could not submit — please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  function regeneratePaper() {
    if (paperDraft.trim() && !window.confirm('Replace the current draft with a fresh AI version? Your edits will be lost.')) return
    generatePaper()
  }

  return (
    <Box sx={{ maxWidth: 760 }}>
      {!isReadOnly && editable && saveState !== 'idle' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mb: 1 }}>
          {saveState === 'saving' ? 'Saving…' : '✓ All changes saved'}
        </Typography>
      )}
      {isReturning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Your mentor sent this back:</strong>
          {mentorNote && <Box sx={{ mt: 0.5 }}><Markdown>{mentorNote}</Markdown></Box>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Revise your work and resubmit when ready.
          </Typography>
        </Alert>
      )}
      {status === 'submitted' && <Alert severity="info" sx={{ mb: 2 }}>Submitted to your mentor for review.</Alert>}
      {(status === 'approved_draft' || status === 'completed') && (
        <Alert severity="success" sx={{ mb: 2 }}>Approved by your mentor.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {view !== 'paper' && (<>
        {sections.map(s => (
          <Box key={s.key} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{s.label}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{s.helper}</Typography>
            <TextField
              fullWidth multiline minRows={s.rows ?? 3}
              value={fields[s.key] ?? ''} onChange={e => updateField(s.key, e.target.value)}
              disabled={isReadOnly}
            />
            {/* Data collection's Google Sheet link lives right after the method field. */}
            {s.key === 'data_collection_method' && (
              <Box sx={{ mt: 1.5, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Data collection table</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Set up your data table in Google Sheets and paste the <strong>editable link</strong> here.
                </Typography>
                <TextField
                  fullWidth size="small" type="url"
                  placeholder="https://docs.google.com/spreadsheets/…"
                  value={dataSheetUrl} onChange={e => { setDataSheetUrl(e.target.value); markStale(s.key) }}
                  disabled={isReadOnly}
                />
              </Box>
            )}
            <GroupFeedback name={s.label.toLowerCase()} disabled={isReadOnly}
              canEvaluate={(fields[s.key] ?? '').trim().length > 5}
              evaluating={evaluating === s.key} feedback={feedback[s.key]} stale={staleSet.has(s.key)}
              onEvaluate={() => evaluate(s.key)} />
          </Box>
        ))}

        {view === 'design' && (
          <JudgeReadinessPanel
            description={isEng
              ? 'This looks at your whole development plan together and scores how much of it could stand up to the questions science-fair judges commonly ask — so you can strengthen the plan (not memorize answers).'
              : 'This looks at your whole experimental design together and scores how much of it could stand up to the questions science-fair judges commonly ask — so you can strengthen the design (not memorize answers).'}
            subject={isEng ? 'development plan' : 'experimental design'}
            canRun={!isReadOnly && judgeAnchor.length >= 10}
            hint={!isReadOnly && judgeAnchor.length < 10
              ? (isEng ? 'Write your design description first.' : 'Write your procedure first.')
              : null}
            loading={judgeLoading}
            onRun={runJudgeReadiness}
            result={judge}
          />
        )}

        {view === 'writeup' && (
          <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
            This tab isn't required to submit — it's framing content for your eventual research paper. Switch to the <strong>Paper</strong> tab once your Design sections are ready.
          </Alert>
        )}

        {/* Submit needs a paper draft, which is generated on the Paper tab —
            until one exists, point there from Design. */}
        {view === 'design' && !isReadOnly && readyToSubmit && !paperDraft.trim() && (
          <Alert severity="success" variant="outlined" sx={{ mt: 1 }}
            action={<Button color="inherit" size="small" onClick={onOpenPaper}>Go to Paper</Button>}>
            Your Design sections are ready. Open the <strong>Paper</strong> tab to generate your draft, then submit to your mentor.
          </Alert>
        )}
      </>)}

      {view === 'paper' && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            Your Design and Write-Up notes are assembled into a draft Research Plan document here. The AI organizes{' '}
            <strong>your own notes</strong> into a first draft — it won't add new facts, materials, or sources. Edit it into
            your own voice, then submit it for mentor review and print a copy for your records.
          </Typography>

          {!paperDraft.trim() ? (
            <Box sx={{ p: 3, borderRadius: 1, bgcolor: 'action.hover', textAlign: 'center' }}>
              {!readyToSubmit ? (
                <Typography variant="body2" color="text.secondary">
                  Finish all the Design sections first, then come back here to generate your draft.
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    When you're ready, generate a first draft from everything you wrote in Design and Write-Up.
                  </Typography>
                  <Button variant="contained" onClick={generatePaper} disabled={generating || isReadOnly}>
                    {generating ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Writing your draft…</> : 'Generate draft paper'}
                  </Button>
                </>
              )}
            </Box>
          ) : (
            <>
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                This is an AI-assembled <strong>first draft</strong> from your Design and Write-Up notes. Make it yours — edit,
                reword, and fix anything before you submit or print.
              </Alert>
              <TextField
                fullWidth multiline minRows={18} value={paperDraft}
                onChange={e => setPaperDraft(e.target.value)} disabled={isReadOnly}
                sx={{ '& textarea': { fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 1.6 } }}
              />
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {!isReadOnly && (
                  <Button variant="outlined" onClick={regeneratePaper} disabled={generating}>
                    {generating ? <><CircularProgress size={14} sx={{ mr: 1 }} />Regenerating…</> : 'Regenerate'}
                  </Button>
                )}
                <Button variant="outlined" onClick={() => printPaper(paperDraft)}>Print for records</Button>
              </Box>

              {!isReadOnly && editable && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Note to your mentor <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {isReturning
                      ? 'What did you change since last time, and where would you like feedback?'
                      : 'What are you most unsure about, or where would you like your mentor to focus?'}
                  </Typography>
                  <TextField
                    fullWidth multiline minRows={2} maxRows={8} size="small"
                    placeholder="e.g. Not sure my procedure is detailed enough — could you check the apparatus section?"
                    value={submissionNote} onChange={e => setSubmissionNote(e.target.value)}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Same Submit on Design and Paper so students find it from either. */}
      {!isReadOnly && (view === 'paper' || view === 'design') && paperDraft.trim() && (
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
          {editable ? (
            <Button variant="contained" onClick={submit} disabled={!readyToSubmit || submitting}>
              {submitting ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Submitting…</>
                : isReturning ? 'Resubmit for mentor review' : 'Submit to mentor'}
            </Button>
          ) : (
            <>
              <Button variant="contained" disabled>{isReturning ? 'Resubmit for mentor review' : 'Submit to mentor'}</Button>
              <Typography variant="caption" color="text.secondary">Only the student can submit — they'll do it from here.</Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  )
}

// ── Mentor Review tab (student-facing status) ───────────────────────────────────
// Note: 'variables_hypothesis' label differs by track; eng-first then sci so
// the more common scientific-track label wins as the generic fallback here
// (this map isn't track-aware — actual comment text is what matters most).
const DESIGN_SECTION_LABELS: Record<string, string> = Object.fromEntries(
  [...SCI_SECTIONS, ...ENG_SECTIONS, ...writeUpSections(true), ...writeUpSections(false)].map(s => [s.key, s.label]),
)

function DesignMentorReviewTab({ status, mentorNote, sectionComments }: {
  status: Status; mentorNote: string | null; sectionComments: Record<string, string>
}) {
  if (status === 'submitted') return <Alert severity="info">Your work is with your mentor for review. You'll see their decision on your dashboard.</Alert>
  const sections = Object.entries(sectionComments).filter(([, v]) => (v ?? '').trim())
  if (status === 'approved_draft' || status === 'completed') {
    return (
      <Alert severity="success">
        <strong>Approved by your mentor.</strong>
        {mentorNote && <Box sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{mentorNote}</Box>}
        {sections.map(([key, comment]) => (
          <Box key={key} sx={{ mt: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{DESIGN_SECTION_LABELS[key] ?? key}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{comment}</Typography>
          </Box>
        ))}
      </Alert>
    )
  }
  if (mentorNote || sections.length > 0) {
    return (
      <Alert severity="warning">
        <strong>Your mentor sent this back with feedback:</strong>
        {mentorNote && <Box sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{mentorNote}</Box>}
        {sections.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            {sections.map(([key, comment]) => (
              <Box key={key} sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>{DESIGN_SECTION_LABELS[key] ?? key}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{comment}</Typography>
              </Box>
            ))}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Revise your work and resubmit.</Typography>
      </Alert>
    )
  }
  return <Alert severity="info">Finish your experimental design and submit it for mentor review from the Design or Paper tab.</Alert>
}

// ── SRC Review tab — form triage & tracking ──────────────────────────────────
// Scientific Review Committee pre-approval: some subjects (human participants,
// vertebrate animals, biological agents, hazards) need SRC sign-off BEFORE any
// experimentation. This tab: (1) two logistics questions the AI can't infer
// from the science, (2) an AI recommendation of which forms apply (unions with
// anything already flagged in Step 4 — step5-recommend-forms), (3) a tracked
// checklist per form, (4) upcoming fair deadlines. See docs/step5-src-forms.md.

type FormStatusValue = 'not_started' | 'downloaded' | 'signed' | 'uploaded'
const FORM_STATUS_LABEL: Record<FormStatusValue, string> = {
  not_started: 'Not started', downloaded: 'Downloaded', signed: 'Signed', uploaded: 'Uploaded',
}
interface FormNeeded { key: string; form: string; label: string; required: boolean; reason: string }
interface SrcRecommendation {
  triggered?: Record<string, boolean>
  forms_needed?: FormNeeded[]
  summary?: string
  ai_disclaimer?: string
  evaluated_at?: string
}
interface SrcFormStatus { status: FormStatusValue; link?: string }

function SrcReviewTab({ projectId, studentId, editable }: {
  projectId: string; studentId: string; editable: boolean
}) {
  const [loading, setLoading] = useState(true)
  const [continuation, setContinuation] = useState(false)
  const [atRri,        setAtRri]        = useState(false)
  const [recommendation, setRecommendation] = useState<SrcRecommendation | null>(null)
  const [formStatus,   setFormStatus]   = useState<Record<string, SrcFormStatus>>({})
  const [running,      setRunning]      = useState(false)
  const [error,        setError]        = useState<string | null>(null)
  const [saveState,    setSaveState]    = useState<'idle' | 'saving' | 'saved'>('idle')

  useEffect(() => {
    let cancelled = false
    async function load() {
      const { data } = await supabase.from('src_review')
        .select('continuation, at_rri, recommendation, form_status')
        .eq('project_id', projectId).maybeSingle()
      if (cancelled) return
      const row = data as { continuation?: boolean; at_rri?: boolean; recommendation?: SrcRecommendation; form_status?: Record<string, SrcFormStatus> } | null
      setContinuation(row?.continuation ?? false)
      setAtRri(row?.at_rri ?? false)
      setRecommendation(row?.recommendation ?? null)
      setFormStatus(row?.form_status ?? {})
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [projectId])

  async function save(extra?: { continuation?: boolean; at_rri?: boolean; recommendation?: SrcRecommendation; form_status?: Record<string, SrcFormStatus> }) {
    if (!editable) return
    setSaveState('saving')
    const { error: upErr } = await supabase.from('src_review').upsert({
      project_id: projectId, student_id: studentId,
      continuation, at_rri: atRri, recommendation, form_status: formStatus,
      ...extra,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id' })
    setSaveState(upErr ? 'idle' : 'saved')
  }

  const firstRun = useRef(true)
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return }
    if (!editable) return
    const t = setTimeout(() => { save() }, 700)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continuation, atRri])

  async function runRecommendation() {
    if (running) return
    setRunning(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step5-recommend-forms', {
        body: { project_id: projectId, student_id: studentId, continuation, at_rri: atRri },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      setRecommendation(data as SrcRecommendation)
      await save({ recommendation: data as SrcRecommendation })
    } catch (e: any) {
      setError(e.message ?? 'Could not run the recommendation — please try again.')
    } finally {
      setRunning(false)
    }
  }

  function updateFormStatus(key: string, patch: Partial<SrcFormStatus>) {
    setFormStatus(prev => {
      const base: SrcFormStatus = prev[key] ?? { status: 'not_started' }
      const next = { ...prev, [key]: { ...base, ...patch } }
      save({ form_status: next })
      return next
    })
  }

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>

  const formsNeeded = recommendation?.forms_needed ?? []

  return (
    <Box sx={{ maxWidth: 760 }}>
      {editable && saveState !== 'idle' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mb: 1 }}>
          {saveState === 'saving' ? 'Saving…' : '✓ All changes saved'}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
        Some science-fair projects need Scientific Review Committee (SRC) approval <strong>before</strong> you start
        experimenting — human participants, vertebrate animals, biological agents, or hazards all trigger this.
        Answer the two questions below, then get a recommendation of which forms apply to your project.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ mb: 3, p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Two quick questions</Typography>
        <FormControlLabel sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}
          control={<Checkbox checked={continuation} disabled={!editable} sx={{ pt: 0 }}
            onChange={e => setContinuation(e.target.checked)} />}
          label={<Typography variant="body2" sx={{ mt: 0.75 }}>Is this project a <strong>continuation</strong> of research you did in a previous year?</Typography>}
        />
        <FormControlLabel sx={{ display: 'flex', alignItems: 'flex-start' }}
          control={<Checkbox checked={atRri} disabled={!editable} sx={{ pt: 0 }}
            onChange={e => setAtRri(e.target.checked)} />}
          label={<Typography variant="body2" sx={{ mt: 0.75 }}>
            Will any part of your experiment happen at a <strong>university, hospital, or professional research lab</strong> (not your school or home)?
          </Typography>}
        />
        {editable && (
          <Button variant="contained" size="small" sx={{ mt: 2 }} onClick={runRecommendation} disabled={running}>
            {running ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Checking…</> : recommendation ? 'Re-check recommendation' : 'Get AI recommendation'}
          </Button>
        )}
      </Box>

      {recommendation && (
        <Box sx={{ mb: 3 }}>
          {recommendation.summary && (
            <Alert severity={formsNeeded.some(f => f.required) ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {recommendation.summary}
            </Alert>
          )}
          {formsNeeded.length === 0 ? (
            <Alert severity="success" variant="outlined">No forms flagged — nothing here needs SRC pre-approval based on what you've written so far.</Alert>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Forms checklist</Typography>
              {formsNeeded.map(f => {
                const fs = formStatus[f.key] ?? { status: 'not_started' as FormStatusValue }
                return (
                  <Box key={f.key} sx={{ mb: 1.5, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{f.form} — {f.label}</Typography>
                      {!f.required && <Typography variant="caption" color="text.secondary">(recommended)</Typography>}
                    </Box>
                    {f.reason && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{f.reason}</Typography>}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Select size="small" value={fs.status} disabled={!editable}
                        onChange={e => updateFormStatus(f.key, { status: e.target.value as FormStatusValue })}
                        sx={{ minWidth: 140 }}>
                        {(Object.keys(FORM_STATUS_LABEL) as FormStatusValue[]).map(s => (
                          <MenuItem key={s} value={s}>{FORM_STATUS_LABEL[s]}</MenuItem>
                        ))}
                      </Select>
                      <TextField
                        size="small" placeholder="Link to signed form (optional)"
                        value={fs.link ?? ''} disabled={!editable}
                        onChange={e => updateFormStatus(f.key, { link: e.target.value })}
                        sx={{ flex: 1, minWidth: 220 }}
                      />
                    </Box>
                  </Box>
                )
              })}
            </>
          )}
          {recommendation.ai_disclaimer && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              ⓘ {recommendation.ai_disclaimer}
            </Typography>
          )}
        </Box>
      )}

      <ImportantDatesCard projectId={projectId} canChange />
    </Box>
  )
}

// ── Learn tab — lesson-6 teaching videos (Scientific track) ─────────────────────
// Same UX as the Step 1–4 Learn tabs: numbered sections with an intro, and
// collapsed video cards that expand one at a time (embed mounts on demand).
// Video IDs from docs/step5-source-content.md (Vimeo, Problem.questionVideo).
// 6.3 "Variables, Groups & Samples" and 6.4 "Data Collection Method" are merged
// into one section below because the source data currently reuses the same
// video id (979411692) for both — a known authoring gap (6.4 needs its own
// clip); split them once that clip is sourced.

const LEARN_SECTIONS = [
  {
    title: 'What Is Experimental Design',
    intro: 'Before you design your own experiment, this lesson introduces what experimental design means and why it\'s the next step after your hypothesis.',
    videos: [
      { title: 'What Is Experimental Design', videoId: '979411534' },
    ],
  },
  {
    title: 'Choosing Your Design Type',
    intro: 'There\'s more than one way to structure an experiment. This lesson walks through the common types of experimental design so you can pick the one that fits your hypothesis.',
    videos: [
      { title: 'Type of Experimental Design', videoId: '979411590' },
    ],
  },
  {
    title: 'Variables, Groups, Samples & Data Collection',
    intro: 'Turn your independent and dependent variables into concrete experimental groups and samples — and plan how you\'ll actually capture your measurements.',
    videos: [
      { title: 'Variables, Experimental Groups, and Samples', videoId: '979411692' },
    ],
  },
  {
    title: 'Designing Your Apparatus & Software',
    intro: 'Design and implement the tools — physical apparatus, sensors, or data-collection software — you\'ll use to take your measurements.',
    videos: [
      { title: 'Design and Implement Apparatus and Software', videoId: '979411797' },
    ],
  },
  {
    title: 'Writing Your Procedure',
    intro: 'Turn your design into a clear, repeatable, step-by-step method that someone else could follow to run your experiment.',
    videos: [
      { title: 'Method and Procedure', videoId: '979411851' },
    ],
  },
  {
    title: 'Presenting Your Experimental Design',
    intro: 'Package your experimental design into the presentation you\'ll submit — this closes out Step 5 alongside your Research Plan Worksheet.',
    videos: [
      { title: 'Present Experimental Design', videoId: '979411888' },
    ],
  },
]

function VimeoEmbed({ videoId, title }: { videoId: string; title: string }) {
  return (
    <Box sx={{ mt: 2, textAlign: 'center' }}>
      <iframe
        title={title}
        src={`https://player.vimeo.com/video/${videoId}?title=0&portrait=0&byline=0`}
        width="640"
        height="360"
        style={{ border: 'none', maxWidth: '100%' }}
        allow="autoplay; fullscreen"
        allowFullScreen
      />
    </Box>
  )
}

function LearnTab() {
  const [openVideo, setOpenVideo] = useState<string | null>(null)
  return (
    <Box sx={{ maxWidth: 760, py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
        Watch these short lessons before designing your experiment. They cover choosing a design type,
        operationalizing your variables into groups and samples, planning data collection, building your
        apparatus, writing your procedure, and presenting the finished design.
      </Typography>
      {LEARN_SECTIONS.map((section, si) => (
        <Box key={si} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {si + 1}. {section.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
            {section.intro}
          </Typography>
          {section.videos.map(v => (
            <Card key={v.videoId} variant="outlined" sx={{ mb: 1.5 }}>
              <CardContent sx={{ py: '12px !important' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                  onClick={() => setOpenVideo(openVideo === v.videoId ? null : v.videoId)}
                >
                  <PlayCircleOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{v.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {openVideo === v.videoId ? 'Hide' : 'Watch'}
                  </Typography>
                </Box>
                {openVideo === v.videoId && <VimeoEmbed videoId={v.videoId} title={v.title} />}
              </CardContent>
            </Card>
          ))}
          {si < LEARN_SECTIONS.length - 1 && <Divider sx={{ mt: 2.5 }} />}
        </Box>
      ))}
    </Box>
  )
}

// ── Learn tab — Engineering Design ("Develop Products") track ───────────────────
// docs/step5-source-content.md flags this as a data gap: Lesson 6's DB content
// is experiment-centric only — there are no sourced Develop-Products videos yet.
// Rather than reuse the Scientific-track videos (wrong content for this track)
// or invent video ids, show an honest placeholder until that content is authored.
function EngLearnTab() {
  return (
    <Box sx={{ maxWidth: 760, py: 1 }}>
      <Alert severity="info">
        Develop-Products lessons for Step 5 haven't been sourced yet — the underlying course content
        (A210A Lesson 6) only covers the Design Experiments track so far. Check back soon.
      </Alert>
    </Box>
  )
}
